<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { ref, computed, getCurrentInstance, onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'
import ToggleStreamModal from '@/components/modals/ToggleStreamModal.vue'
import ConfirmModal from '@/components/modals/ConfirmModal.vue'
import StreamService from '@/services/StreamService'
import { mediaPulseModel } from '@/components/Composable/mediaPulse'
import { useNotification } from "@kyvg/vue3-notification"
import Badge from '../Badge.vue'
import FormButton from '../Atoms/FormButton.vue'
import IconBase from '../icon/IconBase.vue'
import IconBin from '../icon/IconBin.vue'
import IconCamera from '../icon/IconCamera.vue'
import StreamThumbnail from '../ui/StreamThumbnail.vue'
import Tooltip from '@/components/Atoms/Tooltip.vue'

const { notify } = useNotification()

const store = useStore()

const instance = getCurrentInstance();
const root = instance.proxy.$root;

const scopeAlive = ref(true)

const props = defineProps({
	playout: {
		type: Object,
		default() { return {}; }
	},
})

// Media Pulse 
const { subscribeMediaPulse, unsubscribeMediaPulse, mediaPulse } = mediaPulseModel()

// Hooks
onMounted(async () => {
	subscribeMediaPulse(props.playout)
})
onUnmounted(() => {
	unsubscribeMediaPulse()
	scopeAlive.value = false
})

const toggleStatus = (ev) => {
	ev.preventDefault();
	ev.stopPropagation();
	showModal('modalToggleStream' + props.playout._id, '#btnShow')
}
const onDeletePlayout = async () => {
	hideModal('delete-playout-' + props.playout._id);
	try {
		await StreamService.deleteStream(
			props.playout._id
		);
		await store.dispatch('Streams/getStreams', true)
		notify({ group: 'info', text: 'TV Playout deleted.' })
	} catch (err) {
		this.error = err
	}
}

const showDeleteModal = (ev) => {
	ev.preventDefault();
	ev.stopPropagation();
	showModal('delete-playout-' + props.playout._id);
}

const shouldShowCompactModeStreams = computed(() => store.getters['Ui/shouldShowCompactModeStreams'])
const hasDestination = computed(() => !!props.playout?.destination)
const getTooltipText = computed(() => {
	if (hasDestination.value) {
		const linkedStreamName = props.playout.destination;
		return `This livestream cannot be deleted because it is being used as a source in ${linkedStreamName} livestream. To delete this livestream, first remove it as a source from ${linkedStreamName} livestream.`
	}
	return '';
})

</script>
<template>
	<div class="flex relative"
		:class="!shouldShowCompactModeStreams ? 'flex-col bg-surface-2 rounded-lg h-full' : 'flex-row items-center'">
		<router-link class="text-reset hover:no-underline" :class="{'grid gap-3 w-full grid-cols-[1fr_2fr] md:grid-cols-[5rem_2fr_1fr_110px] !py-2 md:!py-0': shouldShowCompactModeStreams}" :to="{ name: 'PlayoutView', params: { tvId: playout._id } }">
			<StreamThumbnail :stream="playout" :media-pulse="mediaPulse"
				:compact-mode="shouldShowCompactModeStreams ? true : false" :class="{ 'self-center': shouldShowCompactModeStreams }" />
			<div :class="!shouldShowCompactModeStreams ? 'pt-2 px-3' : 'py-2 w-2/3 md:w-9/12'">
				<h2 class="text-bace font-medium mb-1">
					{{ playout.name
					}}
				</h2>
				<p class="text-tiny mb-0 flex items-center" :class="playout.destination ? 'text-white' : 'text-surface-7'">
					<icon-base class="mr-2">
						<icon-camera />
					</icon-base>
					{{ playout.destination ? playout.destination : 'No Destination Selected' }}
				</p>
			</div>
			<Badge :type="mediaPulse?.alive ? 'online' : 'offline'" :title="mediaPulse?.alive ? 'Live' : 'Offline'"
				class="inline-flex self-center" :class="!shouldShowCompactModeStreams ? 'absolute top-2 left-3 z-10' : '!mr-auto md:!mr-0 md:!ml-auto'" />
			<div class="text-right flex items-center justify-end" :class="{'px-3 pb-3': !shouldShowCompactModeStreams}">
				<FormButton @click="toggleStatus" v-if="playout.enabled" type="secondary" size="sm" class="mr-2">
					Disable
				</FormButton>
				<FormButton @click="toggleStatus" v-else size="sm" class="mr-2">
					Enable
				</FormButton>
				<Tooltip v-if="hasDestination" :content="getTooltipText">
					<FormButton @click="showDeleteModal" type="danger" size="sm" isIcon :disabled="hasDestination">
						<icon-base>
							<icon-bin />
						</icon-base>
					</FormButton>
				</Tooltip>
				<FormButton 
					v-else @click="showDeleteModal" type="danger" size="sm" isIcon>
					<icon-base>
						<icon-bin />
					</icon-base>
				</FormButton>
			</div>
		</router-link>
		<toggle-stream-modal :stream="playout" @modal-toggle="toggleStatus" />
		<confirm-modal :modalId="'delete-playout-' + playout._id" message="Are you sure you want to delete your TV Playout?"
			modal-type="danger" @modal-confirm="onDeletePlayout" />
	</div>
</template>

<style scoped></style>